import React, { useState } from 'react';
import { Box, Button, Divider, IconButton, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Bookmarks as ViewsIcon, DeleteOutline as DeleteIcon, KeyboardArrowDown as ChevronIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { ProductBrowserSavedView } from '../../utils/useSavedViews';

export interface ViewsMenuProps {
  views: Array<ProductBrowserSavedView & { summary: string }>;
  /** Name of the currently-applied view, if any — lights the button. */
  activeViewName?: string | null;
  onApply: (view: ProductBrowserSavedView) => void;
  onRemove: (id: string) => void;
  /** One-line description of the filters this view would save (join with " · "). */
  currentSummary: string;
  onSave: (name: string) => void;
}

/**
 * A saved view is just the current filter state under a name. Applying one
 * sets every filter at once and lights this button with the view's name.
 */
export default function ViewsMenu({ views, activeViewName, onApply, onRemove, currentSummary, onSave }: ViewsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [name, setName] = useState('');
  const open = Boolean(anchorEl);

  const close = () => {
    setAnchorEl(null);
    setName('');
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    close();
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.875,
          height: 38,
          px: 1.5,
          borderRadius: '4px',
          border: '1px solid',
          borderColor: activeViewName ? (theme: Theme) => alpha(theme.palette.primary.main, 0.28) : 'divider',
          bgcolor: activeViewName ? (theme: Theme) => alpha(theme.palette.primary.main, 0.07) : 'background.paper',
          color: activeViewName ? 'primary.dark' : 'text.secondary',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: '0.84375rem',
          fontWeight: 600,
          '&:hover': { borderColor: 'grey.300' },
        }}
      >
        <ViewsIcon sx={{ fontSize: 16 }} />
        <Box component="span" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeViewName || 'Views'}
        </Box>
        <ChevronIcon sx={{ fontSize: 16, opacity: 0.7 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 330,
              p: 0.75,
              overflow: 'hidden',
              boxShadow: '0 14px 36px rgba(15, 23, 42, 0.15)',
            },
          },
        }}
      >
        <Typography
          variant="overline"
          sx={{ display: 'block', px: 1, pt: 0.5, pb: 0.75, color: 'text.disabled' }}
        >
          Saved views
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', maxHeight: 230, overflowY: 'auto' }}>
          {views.map((view) => {
            const isActive = activeViewName === view.name;
            return (
              <Box
                key={view.id}
                sx={{ display: 'flex', alignItems: 'center', borderRadius: '4px', bgcolor: isActive ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    onApply(view);
                    close();
                  }}
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    borderRadius: '4px',
                    px: 1,
                    py: 0.75,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  <Box component="span" sx={{ fontSize: '0.84375rem', fontWeight: isActive ? 600 : 500, color: isActive ? 'primary.dark' : 'text.primary' }}>
                    {view.name}
                  </Box>
                  <Box component="span" sx={{ fontSize: '0.71875rem', color: 'text.disabled', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                    {view.summary}
                  </Box>
                </Box>
                <Tooltip title="Delete view">
                  <IconButton size="small" aria-label={`Delete saved view ${view.name}`} onClick={() => onRemove(view.id)} sx={{ mr: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
          {views.length === 0 && (
            <Typography variant="body2" sx={{ px: 1, py: 1, color: 'text.disabled', fontSize: '0.8125rem' }}>
              No saved views yet
            </Typography>
          )}
        </Box>
        <Divider sx={{ mt: 0.75, mb: 0.75 }} />
        <Box sx={{ px: 1, pb: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.disabled', lineHeight: 1.45 }}>
            Save the current filters: {currentSummary}
          </Typography>
          <Stack direction="row" spacing={0.75}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={name}
              placeholder="Name this view"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
              inputProps={{ 'aria-label': 'Name this view' }}
            />
            <Button size="small" variant="contained" disabled={!name.trim()} onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
